import React, {useEffect} from 'react';
import {Outlet, useNavigate} from 'react-router';

import {useNavManager} from 'common/contexts/react/nav-manager';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useQueryContextManager} from 'common/contexts/react/query-context-manager';
import {Props as SpaNavManagerProps} from 'common/contexts/nav-manager/types';
import {useDomEvent} from 'common/hooks/useDomEvent';
import {closestReportLink} from 'common/report/util';

function useInitSpaNavManager() {
  const navManager = useNavManager();
  const currentProjectResource = useCurrentProject();
  const queryContextManager = useQueryContextManager();
  const navigate = useNavigate();
  const spaNavManagerProps: SpaNavManagerProps = {
    currentProjectResource,
    queryContextManager,
    navigate,
  };

  useEffect(() => {
    navManager.updateProps(spaNavManagerProps);
  }, Object.values(spaNavManagerProps));
}

function WithGlobalDomEvents(props: React.PropsWithChildren) {
  const navManager = useNavManager();

  const handleReportSwitch = (ev: CustomEvent) => {
    navManager.navigate(ev.detail.newUrlPath, {triggerEvent: ev, keepReportContext: true});
  };
  useDomEvent(window, `reportswitch`, handleReportSwitch as EventListener);

  // TODO: This is needed for links that haven't been updated to use react-router's SPA navigation.
  // Ideally, would refactor these places so this fallback isn't needed anymore. Probably need to add tracking to
  // trace down all callsites.
  const handleLinkClick = (ev: MouseEvent) => {
    const target = ev.composedPath()[0] as HTMLElement | undefined;
    if (!target) {
      return;
    }

    const anchorLink: HTMLAnchorElement = closestReportLink(target);
    // let browser handle open in new tab interaction for a(target=_blank)
    if (anchorLink && !ev.defaultPrevented) {
      const href = anchorLink.getAttribute(`href`)!;
      mixpanel.track(`Global Link Click Handler Triggered`, {
        'Anchor Href': href,
        'Class Names': anchorLink.getAttribute(`class`),
      });
      navManager.navigate(href, {triggerEvent: ev, fromAnchorLink: true, anchorTarget: anchorLink.target});
    }
  };
  useDomEvent(document, `click`, handleLinkClick as EventListener);

  return props.children;
}

/**
 * `useNavigate`/`useLocation` can only be used within react router context. Any page initialization logic that
 * depends on these contexts can be put into here.
 */
export function RoutesLayout() {
  useInitSpaNavManager();
  return (
    <WithGlobalDomEvents>
      <Outlet />
    </WithGlobalDomEvents>
  );
}
